import { BroadcastChannel } from 'broadcast-channel';
import { RxDatabase } from 'rxdb';
import { ReplicationOptions } from 'rxdb/dist/types/types';
import {
	replicateRxCollection,
	RxReplicationState,
} from 'rxdb/plugins/replication';
import {
	BehaviorSubject,
	interval,
	Observable,
	of,
	Subject,
	takeUntil,
	tap,
} from 'rxjs';

export type TColReplicationDescriptor<RxDocType, CheckpointType> = Omit<
	ReplicationOptions<RxDocType, CheckpointType>,
	'collection'
> & { collectionName: string; authOnly?: boolean };

export type TReplications<K extends string = string> = {
	[P in K]: ReplicationController;
};

export class ReplicationController<RxDocType = any, CheckpointType = any> {
	private db: RxDatabase;
	private readonly replicationDescriptor: TColReplicationDescriptor<
		RxDocType,
		CheckpointType
	>;
	private readonly shouldAddRxDBDebugUtils: boolean;

	public replicationState$: BehaviorSubject<null | RxReplicationState<
		RxDocType,
		CheckpointType
	>>;

	private readonly RESYNC_INTERVAL = 1000 * 10; //10sec
	// private readonly RESYNC_INTERVAL = 1000 * 60 * 2; //2min
	private stopResyncIntervalSignal$ = new Subject<void>();
	private isResyncIntervalSet = false;

	/**
	 * This thing is used in order to support multitabs mode.
	 * The `reSync()` method of the RxReplicationState doesn't trigger the replication from a non-leader tab
	 * */
	private resyncChannel = new BroadcastChannel<void>('reSync', {
		type: typeof window === 'undefined' ? 'simulate' : 'native',
		webWorkerSupport: false,
	});

	constructor(
		db: RxDatabase,
		replicationDescriptor: TColReplicationDescriptor<any, any>,
		shouldAddRxDBDebugUtils = false
	) {
		this.db = db;
		this.replicationDescriptor = replicationDescriptor;
		this.shouldAddRxDBDebugUtils = shouldAddRxDBDebugUtils;

		this.replicationState$ = new BehaviorSubject<null | RxReplicationState<
			RxDocType,
			CheckpointType
		>>(null);

		this.resyncChannel.onmessage = () => {
			const replicationState = this.replicationState;

			if (replicationState) {
				replicationState.reSync();
			}
		};
	}

	public get replicationState(): RxReplicationState<
		RxDocType,
		CheckpointType
	> | null {
		return this.replicationState$?.getValue() || null;
	}

	private createReplicationState() {
		const { collectionName, authOnly, ...replicationOptionsFromDescriptor } =
			this.replicationDescriptor;

		const replicationOptions: ReplicationOptions<any, any> = {
			...replicationOptionsFromDescriptor,
			collection: this.db.collections[collectionName],
		};

		return replicateRxCollection(replicationOptions);
	}

	/**
	 * If the replicationState and the collection are ready - start the replication
	 * Otherwise - restart it from scratch
	 * */
	public async start(): Promise<void> {
		let replicationState = this.replicationState;

		if (replicationState) {
			console.log(
				`${replicationState.collection.name}: replication start is not started, because it is already started`
			);
			return;
		}

		replicationState = this.createReplicationState();
		this.replicationState$.next(replicationState);

		await replicationState.start();

		this.startResyncInterval();

		console.log(
			`${replicationState.collection.name}: replication start is triggered`
		);
	}

	/** Stops the replication and removes the collection
	 * This helps to restart the replication from scratch (resets the checkpoints and the data)
	 * */
	public async stop(): Promise<void> {
		const replicationState = this.replicationState;
		const collectionName = this.replicationDescriptor.collectionName;

		if (!replicationState) {
			return Promise.resolve();
		}

		this.stopResyncInterval();

		await replicationState.remove();

		this.replicationState$.next(null);

		console.log(`${collectionName}: replication is stopped`);
	}

	public startDevLogging() {
		const replicationState = this.replicationState;

		/** TODO this doesn't work because the replicationState is not created yet */
		if (!replicationState) {
			return;
		}

		if (this.shouldAddRxDBDebugUtils) {
			/** For debug purposes */
			replicationState.received$.subscribe((value) => {
				console.log(`${replicationState.collection.name} received$:`, value);
			});
			replicationState.error$.subscribe((error) => {
				console.log(`${replicationState.collection.name} error$:`);
				console.error(error);
			});
		}
	}

	public get isReplicating$(): Observable<boolean> {
		return this.replicationState?.active$ || of(false);
	}

	public reSync() {
		const replicationState = this.replicationState;

		if (!replicationState) {
			return;
		}

		replicationState.reSync();
	}

	get isAuthProtected(): boolean {
		return Boolean(this.replicationDescriptor.authOnly);
	}

	/** TODO resyncing even in the native background??? */
	private async startResyncInterval(): Promise<void> {
		if (this.isResyncIntervalSet) {
			return;
		}

		await this.db.waitForLeadership();

		this.isResyncIntervalSet = true;

		interval(this.RESYNC_INTERVAL)
			.pipe(
				tap(() => {
					this.reSync();
				}),
				takeUntil(this.stopResyncIntervalSignal$)
			)
			.subscribe();
	}

	private stopResyncInterval(): void {
		if (!this.isResyncIntervalSet) {
			return;
		}

		this.isResyncIntervalSet = false;
		this.stopResyncIntervalSignal$.next();
	}
}
